import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarPicker } from '../components/ui/calendar';
import { CalendarIcon } from 'lucide-react';
import {
  Clock, Plus, Trash2, Edit2, Calendar, User, Briefcase,
  Filter, X, ChevronDown, AlertCircle, Users, ChevronRight, Check,
  Download, Upload, FileText, DollarSign, FileSpreadsheet, Truck,
  Paperclip, Image, Eye, Loader2
} from 'lucide-react';
import { format, subDays, subMonths, startOfMonth, endOfMonth, parseISO, isValid } from 'date-fns';
import api from '../lib/api';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';

export default function TimeTracking() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [loading, setLoading] = useState(true);
  const [activeJobs, setActiveJobs] = useState([]);
  const [allEmployeesList, setAllEmployeesList] = useState([]);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [employeeHoursSummary, setEmployeeHoursSummary] = useState(null);
  const [expandedEmployee, setExpandedEmployee] = useState(null);
  
  // Filters
  const [selectedJob, setSelectedJob] = useState('all');
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [formData, setFormData] = useState({
    job_id: '',
    employee_ids: [], // Changed to array for multiple employees
    start_date: format(new Date(), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd'),
    selected_days: [0, 1, 2, 3, 4, 5, 6], // 0=Sunday, 1=Monday, ..., 6=Saturday - all selected by default
    start_time: '09:00',
    end_time: '17:00',
    notes: '',
    // New timesheet fields
    perdiem: '',
    co_worker: '',
    truck_number: '',
    odometer: '',
    wex_receipts: '',
    po_number: '',
    job_location: '',
    // Non-billable tracking
    non_billable_hours: '',
    non_billable_reason: '',
    // Mileage tracking
    starting_mileage: '',
    miles_driven: '',
    mileage_charged: '',
    // Reimbursed expenses (array of expense items)
    expenses: []
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploadingReceipt, setUploadingReceipt] = useState(null); // Index of expense being uploaded
  
  // Payroll export dialog state
  const [payrollDialogOpen, setPayrollDialogOpen] = useState(false);
  const [payrollExporting, setPayrollExporting] = useState(false);
  const [payrollDates, setPayrollDates] = useState({
    start: format(startOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd'),
    end: format(endOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd')
  });
  
  // Timesheet export dialog state
  const [timesheetDialogOpen, setTimesheetDialogOpen] = useState(false);
  const [timesheetExporting, setTimesheetExporting] = useState(false);
  const [timesheetData, setTimesheetData] = useState({
    employee_id: '',
    week_ending: format(new Date(), 'yyyy-MM-dd')
  });
  const [timesheetPreview, setTimesheetPreview] = useState(null);

  const expensesFormTotal = useMemo(
    () => formData.expenses.reduce((sum, e) => sum + (e.amount || 0), 0),
    [formData.expenses]
  );

  const sortedPreviewExpenses = useMemo(
    () => [...(timesheetPreview?.expenses || [])].sort((a, b) => (a.date || '').localeCompare(b.date || '')),
    [timesheetPreview]
  );
  
  // Employee timesheet submission state
  const [mySubmissions, setMySubmissions] = useState([]);
  const [correctionDialogOpen, setCorrectionDialogOpen] = useState(false);
  const [correctionReason, setCorrectionReason] = useState('');
  const [requestingCorrection, setRequestingCorrection] = useState(false);
  const [submitWeekEnding, setSubmitWeekEnding] = useState(() => {
    // Default to the most recent Sunday
    const d = new Date();
    const day = d.getDay(); // 0=Sun
    if (day !== 0) d.setDate(d.getDate() + (7 - day));
    return format(d, 'yyyy-MM-dd');
  });
  const [submittingWeek, setSubmittingWeek] = useState(false);
  
  // Day names for the checkbox UI
  const dayNames = [
    { value: 0, label: 'Sun', fullName: 'Sunday' },
    { value: 1, label: 'Mon', fullName: 'Monday' },
    { value: 2, label: 'Tue', fullName: 'Tuesday' },
    { value: 3, label: 'Wed', fullName: 'Wednesday' },
    { value: 4, label: 'Thu', fullName: 'Thursday' },
    { value: 5, label: 'Fri', fullName: 'Friday' },
    { value: 6, label: 'Sat', fullName: 'Saturday' }
  ];
  
  // Get unique employees from active jobs
  const allEmployees = React.useMemo(() => {
    // Use allEmployeesList if available, otherwise fallback to employees from jobs
    if (allEmployeesList.length > 0) {
      return allEmployeesList;
    }
    const empMap = new Map();
    activeJobs.forEach(job => {
      (job.assigned_employees || []).forEach(emp => {
        empMap.set(emp.id, emp);
      });
    });
    return Array.from(empMap.values());
  }, [activeJobs, allEmployeesList]);
  
  // "No Job" sentinel value used in the Job select. On submit we translate it
  // into a real null `job_id` before sending to the backend so overhead time
  // and reimbursement-only entries aren't tied to any customer job.
  const NO_JOB = 'no_job';
  const isNoJob = formData.job_id === NO_JOB;

  // Get employees for selected job.
  // - Real job selected → only employees assigned to that job.
  // - "No Job" selected → admins pick from all employees; non-admins only see themselves.
  // - Nothing selected → empty list.
  const jobEmployees = React.useMemo(() => {
    if (!formData.job_id) return [];
    if (isNoJob) {
      if (!isAdmin && user?.id) {
        return [{ id: user.id, name: user.name || 'Me' }];
      }
      return allEmployees.map(e => ({ id: e.id, name: e.name }));
    }
    const job = activeJobs.find(j => j.id === formData.job_id);
    return job?.assigned_employees || [];
  }, [formData.job_id, activeJobs, isNoJob, isAdmin, user?.id, user?.name, allEmployees]);

  useEffect(() => {
    fetchActiveJobs();
    if (isAdmin) {
      fetchAllEmployees();
    }
    fetchMySubmissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchEntries();
    fetchSummary();
    if (isAdmin) {
      fetchEmployeeHoursSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedJob, selectedEmployee, startDate, endDate]);

  const fetchActiveJobs = async () => {
    try {
      // Fetch all jobs (include_all=true) so admins can track time for any job
      const res = await api.get('/time-tracking/active-jobs?include_all=true');
      setActiveJobs(res.data.jobs || []);
    } catch (error) {
      console.error('Failed to fetch active jobs:', error);
      toast.error('Failed to load active jobs');
    }
  };

  const fetchAllEmployees = async () => {
    try {
      const res = await api.get('/time-tracking/all-employees');
      setAllEmployeesList(res.data.employees || []);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const fetchMySubmissions = async () => {
    try {
      const res = await api.get('/time-tracking/submissions');
      setMySubmissions(res.data?.submissions || []);
    } catch (error) {
      console.error('Failed to fetch submissions:', error);
    }
  };

  // Submission state for the currently-selected week (for employees)
  const submissionForSelectedWeek = React.useMemo(() => {
    if (!submitWeekEnding) return null;
    return mySubmissions.find(s => s.week_ending === submitWeekEnding) || null;
  }, [mySubmissions, submitWeekEnding]);

  const handleRequestCorrection = async () => {
    if (!correctionReason.trim()) {
      toast.error('Please describe what needs to be corrected');
      return;
    }
    setRequestingCorrection(true);
    try {
      await api.post(`/time-tracking/submissions/${submissionForSelectedWeek.id}/request-correction`, {
        reason: correctionReason.trim(),
      });
      toast.success('Correction request sent — an admin will review it');
      setCorrectionDialogOpen(false);
      fetchMySubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request correction');
    } finally {
      setRequestingCorrection(false);
    }
  };

  const handleSubmitWeek = async () => {
    if (!submitWeekEnding) {
      toast.error('Pick a week-ending date first');
      return;
    }
    setSubmittingWeek(true);
    try {
      const res = await api.post('/time-tracking/submissions', { week_ending: submitWeekEnding });
      toast.success(`Submitted ${res.data.entry_count} entries for week ending ${res.data.week_ending}.`);
      fetchMySubmissions();
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit timesheet');
    } finally {
      setSubmittingWeek(false);
    }
  };

  const fetchEmployeeHoursSummary = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const res = await api.get(`/time-tracking/employee-hours-summary?${params}`);
      setEmployeeHoursSummary(res.data);
    } catch (error) {
      console.error('Failed to fetch employee hours summary:', error);
      // Don't leave the UI stuck on a loading skeleton if the API errors.
      // Render an empty state instead.
      setEmployeeHoursSummary({ employees: [], total_hours: 0, total_employees: 0 });
    }
  };

  // Upload receipt for an expense item
  const handleReceiptUpload = async (index, file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload an image (JPEG, PNG, GIF, WebP) or PDF.');
      return;
    }
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }
    
    setUploadingReceipt(index);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const res = await api.post('/time-tracking/receipts/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Update the expense item with receipt info
      const newExpenses = [...formData.expenses];
      newExpenses[index] = {
        ...newExpenses[index],
        receipt_id: res.data.receipt_id,
        receipt_filename: res.data.filename,
        receipt_url: res.data.url
      };
      setFormData({ ...formData, expenses: newExpenses });
      
      toast.success('Receipt uploaded successfully');
    } catch (error) {
      console.error('Failed to upload receipt:', error);
      toast.error('Failed to upload receipt');
    } finally {
      setUploadingReceipt(null);
    }
  };

  // Remove receipt from an expense item
  const handleRemoveReceipt = async (index) => {
    const expense = formData.expenses[index];
    if (!expense.receipt_id) return;
    
    try {
      await api.delete(`/time-tracking/receipts/${expense.receipt_id}`);
      
      const newExpenses = [...formData.expenses];
      newExpenses[index] = {
        ...newExpenses[index],
        receipt_id: null,
        receipt_filename: null,
        receipt_url: null
      };
      setFormData({ ...formData, expenses: newExpenses });
      
      toast.success('Receipt removed');
    } catch (error) {
      console.error('Failed to remove receipt:', error);
      toast.error('Failed to remove receipt');
    }
  };

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedJob !== 'all') params.append('job_id', selectedJob);
      if (selectedEmployee !== 'all') params.append('employee_id', selectedEmployee);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const res = await api.get(`/time-tracking/entries?${params}`);
      setEntries(res.data.entries || []);
    } catch (error) {
      console.error('Failed to fetch entries:', error);
      toast.error('Failed to load time entries');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const res = await api.get(`/time-tracking/summary?${params}`);
      setSummary(res.data);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
    }
  };

  const handleOpenDialog = (entry = null) => {
    if (entry) {
      setEditingEntry(entry);
      setFormData({
        job_id: entry.job_id ?? NO_JOB,
        employee_ids: [entry.employee_id], // Single employee for editing
        start_date: entry.date,
        end_date: entry.date,
        selected_days: [0, 1, 2, 3, 4, 5, 6],
        start_time: entry.start_time,
        end_time: entry.end_time,
        notes: entry.notes || '',
        // Load existing timesheet fields (use ?? to preserve 0 values)
        perdiem: entry.perdiem ?? '',
        co_worker: entry.co_worker ?? '',
        truck_number: entry.truck_number ?? '',
        odometer: entry.odometer ?? '',
        wex_receipts: entry.wex_receipts ?? '',
        po_number: entry.po_number ?? '',
        job_location: entry.job_location ?? '',
        non_billable_hours: entry.non_billable_hours ?? '',
        non_billable_reason: entry.non_billable_reason ?? '',
        // Mileage tracking
        starting_mileage: entry.starting_mileage ?? '',
        miles_driven: entry.miles_driven ?? '',
        mileage_charged: entry.mileage_charged ?? '',
        // Reimbursed expenses
        expenses: entry.expenses ?? []
      });
    } else {
      setEditingEntry(null);
      setFormData({
        job_id: '',
        employee_ids: !isAdmin && user?.id ? [user.id] : [],
        start_date: format(new Date(), 'yyyy-MM-dd'),
        end_date: format(new Date(), 'yyyy-MM-dd'),
        selected_days: [0, 1, 2, 3, 4, 5, 6], // All days selected by default (0=Sun, 6=Sat)
        start_time: '09:00',
        end_time: '17:00',
        notes: '',
        // Timesheet fields
        perdiem: '',
        co_worker: '',
        truck_number: '',
        odometer: '',
        wex_receipts: '',
        po_number: '',
        job_location: '',
        // Non-billable tracking
        non_billable_hours: '',
        non_billable_reason: '',
        // Mileage tracking
        starting_mileage: '',
        miles_driven: '',
        mileage_charged: '',
        // Reimbursed expenses
        expenses: []
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingEntry(null);
  };

  const handleEmployeeToggle = (employeeId) => {
    setFormData(prev => {
      const isSelected = prev.employee_ids.includes(employeeId);
      return {
        ...prev,
        employee_ids: isSelected
          ? prev.employee_ids.filter(id => id !== employeeId)
          : [...prev.employee_ids, employeeId]
      };
    });
  };

  const handleSelectAllEmployees = () => {
    if (formData.employee_ids.length === jobEmployees.length) {
      // Deselect all
      setFormData(prev => ({ ...prev, employee_ids: [] }));
    } else {
      // Select all
      setFormData(prev => ({ ...prev, employee_ids: jobEmployees.map(e => e.id) }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.job_id || formData.employee_ids.length === 0 || !formData.start_date || !formData.end_date || !formData.start_time || !formData.end_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    // For "No Job" entries, non-billable hours OR at least one expense is required.
    if (isNoJob) {
      const nbh = parseFloat(formData.non_billable_hours) || 0;
      const hasExpense = (formData.expenses || []).length > 0;
      if (nbh <= 0 && !hasExpense) {
        toast.error("'No Job' entries require Non-Billable hours or at least one Reimbursed Expense.");
        return;
      }
    }

    // Validate date range
    if (formData.start_date > formData.end_date) {
      toast.error('End date must be on or after start date');
      return;
    }

    // Validate at least one day is selected (for new entries with date range)
    if (!editingEntry && formData.start_date !== formData.end_date && formData.selected_days.length === 0) {
      toast.error('Please select at least one day of the week');
      return;
    }
    
    setSubmitting(true);
    try {
      if (editingEntry) {
        // Editing - only update single entry (use start_date as the date)
        await api.put(`/time-tracking/entries/${editingEntry.id}`, {
          date: formData.start_date,
          start_time: formData.start_time,
          end_time: formData.end_time,
          notes: formData.notes,
          // Timesheet fields
          perdiem: formData.perdiem ? parseFloat(formData.perdiem) : null,
          co_worker: formData.co_worker || null,
          truck_number: formData.truck_number || null,
          odometer: formData.odometer ? parseInt(formData.odometer) : null,
          wex_receipts: formData.wex_receipts || null,
          po_number: formData.po_number || null,
          job_location: formData.job_location || null,
          // Non-billable tracking
          non_billable_hours: formData.non_billable_hours ? parseFloat(formData.non_billable_hours) : null,
          non_billable_reason: formData.non_billable_reason || null,
          // Mileage tracking
          starting_mileage: formData.starting_mileage ? parseInt(formData.starting_mileage) : null,
          miles_driven: formData.miles_driven ? parseFloat(formData.miles_driven) : null,
          mileage_charged: formData.mileage_charged ? parseFloat(formData.mileage_charged) : null,
          // Reimbursed expenses
          expenses: formData.expenses.length > 0 ? formData.expenses : null
        });
        toast.success('Time entry updated');
      } else {
        // Creating - generate dates in range, filtered by selected days of week.
        // IMPORTANT: parse the YYYY-MM-DD strings as LOCAL dates. `new Date('2026-06-14')`
        // would be parsed as UTC midnight, which in CDT becomes Sat Jun 13 7 PM — causing
        // getDay() to return the wrong weekday and format() to emit the wrong yyyy-MM-dd.
        const [sy, sm, sd] = formData.start_date.split('-').map(Number);
        const [ey, em, ed] = formData.end_date.split('-').map(Number);
        const dates = [];
        let currentDate = new Date(sy, sm - 1, sd);
        const endDateObj = new Date(ey, em - 1, ed);
        
        while (currentDate <= endDateObj) {
          const dayOfWeek = currentDate.getDay(); // 0=Sunday, 1=Monday, etc.
          // Only include dates that match selected days of the week
          if (formData.selected_days.includes(dayOfWeek)) {
            dates.push(format(currentDate, 'yyyy-MM-dd'));
          }
          currentDate.setDate(currentDate.getDate() + 1);
        }

        if (dates.length === 0) {
          toast.error('No matching days found in the selected date range');
          setSubmitting(false);
          return;
        }
        
        // Create entry for each employee AND each date.
        // Expenses are independent of time-entry days — attach the entire expenses
        // list to ONLY the first entry per employee (so totals aren't multiplied by
        // the number of days in the range).
        const allEntries = [];
        for (const empId of formData.employee_ids) {
          dates.forEach((date, idx) => {
            allEntries.push({ employee_id: empId, date, isFirstForEmployee: idx === 0 });
          });
        }
        
        const results = await Promise.allSettled(
          allEntries.map(entry =>
            api.post('/time-tracking/entries', {
              job_id: isNoJob ? null : formData.job_id,
              employee_id: entry.employee_id,
              date: entry.date,
              start_time: formData.start_time,
              end_time: formData.end_time,
              notes: formData.notes,
              // Timesheet fields
              perdiem: formData.perdiem ? parseFloat(formData.perdiem) : null,
              co_worker: formData.co_worker || null,
              truck_number: formData.truck_number || null,
              odometer: formData.odometer ? parseInt(formData.odometer) : null,
              wex_receipts: formData.wex_receipts || null,
              po_number: formData.po_number || null,
              job_location: formData.job_location || null,
              // Non-billable tracking
              non_billable_hours: formData.non_billable_hours ? parseFloat(formData.non_billable_hours) : null,
              non_billable_reason: formData.non_billable_reason || null,
              // Mileage tracking
              starting_mileage: formData.starting_mileage ? parseInt(formData.starting_mileage) : null,
              miles_driven: formData.miles_driven ? parseFloat(formData.miles_driven) : null,
              mileage_charged: formData.mileage_charged ? parseFloat(formData.mileage_charged) : null,
              // Reimbursed expenses — only on the first entry per employee
              expenses: (entry.isFirstForEmployee && formData.expenses.length > 0)
                ? formData.expenses
                : null
            })
          )
        );
        
        const successful = results.filter(r => r.status === 'fulfilled').length;
        const failed = results.filter(r => r.status === 'rejected').length;
        
        if (failed > 0) {
          const errors = results
            .filter(r => r.status === 'rejected')
            .map(r => r.reason?.response?.data?.detail || 'Unknown error');
          
          if (successful > 0) {
            toast.warning(`Created ${successful} entries. ${failed} failed: ${errors[0]}`);
          } else {
            toast.error(`Failed to create entries: ${errors[0]}`);
          }
        } else {
          const daysCount = dates.length;
          const empCount = formData.employee_ids.length;
          toast.success(`Created ${successful} time entries (${empCount} employee${empCount > 1 ? 's' : ''} × ${daysCount} day${daysCount > 1 ? 's' : ''})`);
        }
      }
      
      handleCloseDialog();
      fetchEntries();
      fetchSummary();
      fetchEmployeeHoursSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save time entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this time entry?')) return;
    
    try {
      await api.delete(`/time-tracking/entries/${entryId}`);
      toast.success('Time entry deleted');
      fetchEntries();
      fetchSummary();
      fetchEmployeeHoursSummary();
    } catch (error) {
      toast.error('Failed to delete time entry');
    }
  };

  const calculateHours = (start, end) => {
    const [startH, startM] = start.split(':').map(Number);
    const [endH, endM] = end.split(':').map(Number);
    let startMinutes = startH * 60 + startM;
    let endMinutes = endH * 60 + endM;
    if (endMinutes < startMinutes) endMinutes += 24 * 60;
    return ((endMinutes - startMinutes) / 60).toFixed(2);
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const handleExportEntries = async () => {
    try {
      const res = await api.get('/time-tracking/export');
      const data = res.data;
      
      // Create downloadable JSON file
      const blob = new Blob([JSON.stringify(data.entries, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `time_entries_export_${format(new Date(), 'yyyy-MM-dd')}.json`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${data.total} time entries`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export time entries');
    }
  };

  const handleImportEntries = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      const text = await file.text();
      const entries = JSON.parse(text);
      
      if (!Array.isArray(entries)) {
        toast.error('Invalid file format. Expected an array of time entries.');
        return;
      }
      
      const res = await api.post('/time-tracking/import', entries);
      const result = res.data;
      
      if (result.imported > 0) {
        toast.success(`Imported ${result.imported} entries${result.skipped > 0 ? `, ${result.skipped} skipped (duplicates)` : ''}`);
        fetchEntries();
        fetchSummary();
        fetchEmployeeHoursSummary();
      } else if (result.skipped > 0) {
        toast.info(`All ${result.skipped} entries already exist`);
      } else {
        toast.warning('No entries were imported');
      }
      
      if (result.errors?.length > 0) {
        console.error('Import errors:', result.errors);
      }
    } catch (error) {
      console.error('Import failed:', error);
      toast.error('Failed to import time entries. Check file format.');
    }
    
    // Reset file input
    event.target.value = '';
  };

  // Payroll Report Export Functions
  const handleExportPayrollCSV = async () => {
    setPayrollExporting(true);
    try {
      const res = await api.get('/time-tracking/payroll-report', {
        params: {
          start_date: payrollDates.start,
          end_date: payrollDates.end,
          format: 'csv'
        },
        responseType: 'blob'
      });
      
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payroll_report_${payrollDates.start}_to_${payrollDates.end}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success('Payroll report exported as CSV');
      setPayrollDialogOpen(false);
    } catch (error) {
      console.error('CSV export failed:', error);
      toast.error('Failed to export payroll report');
    } finally {
      setPayrollExporting(false);
    }
  };

  const handleExportPayrollPDF = async () => {
    setPayrollExporting(true);
    try {
      const res = await api.get('/time-tracking/payroll-report', {
        params: {
          start_date: payrollDates.start,
          end_date: payrollDates.end,
          format: 'json'
        }
      });
      
      const data = res.data;
      
      // Create PDF document
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Title
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text('Payroll Report', pageWidth / 2, 20, { align: 'center' });
      
      // Period
      doc.setFontSize(12);
      doc.setFont(undefined, 'normal');
      doc.text(`Period: ${format(new Date(payrollDates.start), 'MMM d, yyyy')} - ${format(new Date(payrollDates.end), 'MMM d, yyyy')}`, pageWidth / 2, 28, { align: 'center' });
      doc.text(`Generated: ${format(new Date(), 'MMM d, yyyy HH:mm')}`, pageWidth / 2, 34, { align: 'center' });
      
      // Summary box
      doc.setFillColor(240, 240, 240);
      doc.rect(14, 40, pageWidth - 28, 25, 'F');
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text('SUMMARY', 18, 48);
      doc.setFont(undefined, 'normal');
      doc.text(`Total Employees: ${data.summary.employee_count}`, 18, 55);
      doc.text(`Total Entries: ${data.summary.total_entries}`, 70, 55);
      doc.text(`Regular Hours: ${data.summary.total_regular_hours}h`, 120, 55);
      doc.text(`Overtime Hours: ${data.summary.total_overtime_hours}h`, 18, 62);
      doc.text(`Total Hours: ${data.summary.total_hours}h`, 70, 62);
      
      // Employee Summary Table
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text('Employee Summary', 14, 75);
      
      const employeeTableData = data.employees.map(emp => [
        emp.employee_name,
        emp.days_worked,
        emp.regular_hours.toFixed(2),
        emp.overtime_hours.toFixed(2),
        emp.total_hours.toFixed(2),
        emp.hourly_rate ? `$${emp.hourly_rate.toFixed(2)}` : 'N/A',
        emp.total_pay ? `$${emp.total_pay.toFixed(2)}` : 'N/A'
      ]);
      
      doc.autoTable({
        startY: 80,
        head: [['Employee', 'Days', 'Regular', 'Overtime', 'Total', 'Rate', 'Pay']],
        body: employeeTableData,
        theme: 'striped',
        headStyles: { fillColor: [59, 130, 246], textColor: 255 },
        styles: { fontSize: 9, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 45 },
          1: { cellWidth: 18, halign: 'center' },
          2: { cellWidth: 22, halign: 'right' },
          3: { cellWidth: 22, halign: 'right' },
          4: { cellWidth: 22, halign: 'right' },
          5: { cellWidth: 22, halign: 'right' },
          6: { cellWidth: 25, halign: 'right' }
        }
      });
      
      // Add detailed breakdown if there's room or on new page
      if (data.employees.length > 0) {
        let yPos = doc.lastAutoTable.finalY + 15;
        
        // Check if we need a new page
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }
        
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('Detailed Breakdown by Job', 14, yPos);
        
        const detailTableData = [];
        data.employees.forEach(emp => {
          emp.jobs.forEach(job => {
            detailTableData.push([
              emp.employee_name,
              job.job_title.substring(0, 25),
              job.cost_center || 'N/A',
              job.regular_hours.toFixed(2),
              job.overtime_hours.toFixed(2),
              job.total_hours.toFixed(2)
            ]);
          });
        });
        
        doc.autoTable({
          startY: yPos + 5,
          head: [['Employee', 'Job', 'Division', 'Regular', 'OT', 'Total']],
          body: detailTableData,
          theme: 'striped',
          headStyles: { fillColor: [59, 130, 246], textColor: 255 },
          styles: { fontSize: 8, cellPadding: 2 },
          columnStyles: {
            0: { cellWidth: 40 },
            1: { cellWidth: 50 },
            2: { cellWidth: 30 },
            3: { cellWidth: 20, halign: 'right' },
            4: { cellWidth: 20, halign: 'right' },
            5: { cellWidth: 20, halign: 'right' }
          }
        });
      }
      
      // Save PDF
      doc.save(`payroll_report_${payrollDates.start}_to_${payrollDates.end}.pdf`);
      
      toast.success('Payroll report exported as PDF');
      setPayrollDialogOpen(false);
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error('Failed to export payroll report');
    } finally {
      setPayrollExporting(false);
    }
  };

  // Quick date range presets for payroll
  const setPayrollPreset = (preset) => {
    const today = new Date();
    let start, end;
    
    switch (preset) {
      case 'last-month':
        start = startOfMonth(subMonths(today, 1));
        end = endOfMonth(subMonths(today, 1));
        break;
      case 'this-month':
        start = startOfMonth(today);
        end = today;
        break;
      case 'last-2-weeks':
        start = subDays(today, 14);
        end = today;
        break;
      case 'last-week':
        start = subDays(today, 7);
        end = today;
        break;
      default:
        return;
    }
    
    setPayrollDates({
      start: format(start, 'yyyy-MM-dd'),
      end: format(end, 'yyyy-MM-dd')
    });
  };

  // Timesheet Export Functions
  const handleTimesheetPreview = async () => {
    if (!timesheetData.employee_id || !timesheetData.week_ending) {
      toast.error('Please select an employee and week ending date');
      return;
    }
    
    try {
      const res = await api.get(`/time-tracking/timesheet-preview/${timesheetData.employee_id}`, {
        params: { week_ending: timesheetData.week_ending }
      });
      setTimesheetPreview(res.data);
    } catch (error) {
      console.error('Failed to load timesheet preview:', error);
      toast.error('Failed to load timesheet preview');
    }
  };

  const handleExportTimesheet = async () => {
    if (!timesheetData.employee_id || !timesheetData.week_ending) {
      toast.error('Please select an employee and week ending date');
      return;
    }
    
    setTimesheetExporting(true);
    try {
      const res = await api.get(`/time-tracking/export-timesheet/${timesheetData.employee_id}`, {
        params: { week_ending: timesheetData.week_ending },
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([res.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Get employee name for filename
      const emp = allEmployees.find(e => e.id === timesheetData.employee_id);
      const empName = emp?.name?.replace(/\s+/g, '_') || 'employee';
      a.download = `timesheet_${empName}_${timesheetData.week_ending}.xlsx`;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Timesheet exported successfully');
      setTimesheetDialogOpen(false);
    } catch (error) {
      console.error('Failed to export timesheet:', error);
      toast.error('Failed to export timesheet');
    } finally {
      setTimesheetExporting(false);
    }
  };

  // Get the Sunday of the current week for timesheet
  const getWeekEndingSunday = () => {
    const today = new Date();
    const day = today.getDay(); // 0 = Sunday
    const diff = day === 0 ? 0 : 7 - day;
    const sunday = new Date(today);
    sunday.setDate(today.getDate() + diff);
    return format(sunday, 'yyyy-MM-dd');
  };


  return (
    <div className="space-y-6 animate-fade-in" data-testid="time-tracking-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">Time Tracking</h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">
            Track employee hours for active jobs
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="default" 
            onClick={() => {
              setTimesheetData({ 
                employee_id: isAdmin ? '' : (user?.id || ''), 
                week_ending: getWeekEndingSunday() 
              });
              setTimesheetPreview(null);
              setTimesheetDialogOpen(true);
            }} 
            data-testid="export-timesheet-btn"
            className="bg-blue-600 hover:bg-blue-700"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            {isAdmin ? 'Generate Timesheet' : 'My Timesheet'}
          </Button>
          {isAdmin && (
            <>
              <Button 
                variant="default" 
                onClick={() => setPayrollDialogOpen(true)} 
                data-testid="export-payroll-btn"
                className="bg-green-600 hover:bg-green-700"
              >
                <DollarSign className="w-4 h-4 mr-2" />
                Export Payroll
              </Button>
              <Button variant="outline" onClick={handleExportEntries} data-testid="export-entries-btn">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
              <Button variant="outline" onClick={() => document.getElementById('import-file').click()} data-testid="import-entries-btn">
                <Upload className="w-4 h-4 mr-2" />
                Import
              </Button>
              <input
                id="import-file"
                type="file"
                accept=".json"
                className="hidden"
                onChange={handleImportEntries}
              />
              <Button variant="outline" asChild data-testid="approvals-link-btn">
                <Link to="/timesheet-approvals">
                  <Check className="w-4 h-4 mr-2" />
                  Approvals
                </Link>
              </Button>
            </>
          )}
          <Button onClick={() => handleOpenDialog()} data-testid="add-entry-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Time Entry
          </Button>
        </div>
      </div>

      {/* Employee submission panel */}
      {!isAdmin && (
        <Card data-testid="my-timesheet-submission-panel">
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-end gap-3">
            <div className="flex-1">
              <Label htmlFor="submit-week-ending" className="text-sm">Week ending (Sunday)</Label>
              <Input
                id="submit-week-ending"
                type="date"
                value={submitWeekEnding}
                onChange={(e) => setSubmitWeekEnding(e.target.value)}
                data-testid="submit-week-input"
              />
            </div>
            <div className="flex items-center gap-2">
              {submissionForSelectedWeek && (
                <Badge
                  variant="outline"
                  className={
                    submissionForSelectedWeek.status === 'approved' ? 'bg-green-50 text-green-700 border-green-200' :
                    submissionForSelectedWeek.status === 'submitted' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                    'bg-red-50 text-red-700 border-red-200'
                  }
                  data-testid="submission-status-badge"
                >
                  {submissionForSelectedWeek.status.charAt(0).toUpperCase() + submissionForSelectedWeek.status.slice(1)}
                </Badge>
              )}
              <Button
                onClick={handleSubmitWeek}
                disabled={submittingWeek || (submissionForSelectedWeek && submissionForSelectedWeek.status === 'submitted') || (submissionForSelectedWeek && submissionForSelectedWeek.status === 'approved')}
                data-testid="submit-week-btn"
              >
                {submittingWeek ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                {submissionForSelectedWeek?.status === 'approved'
                  ? 'Approved'
                  : submissionForSelectedWeek?.status === 'submitted'
                    ? 'Awaiting Approval'
                    : submissionForSelectedWeek?.status === 'rejected'
                      ? 'Resubmit Week'
                      : 'Submit Week for Approval'}
              </Button>
              {submissionForSelectedWeek?.status === 'approved' && !submissionForSelectedWeek.correction_requested && (
                <Button
                  variant="outline"
                  onClick={() => { setCorrectionReason(''); setCorrectionDialogOpen(true); }}
                  data-testid="request-correction-btn"
                >
                  Request Correction
                </Button>
              )}
              {submissionForSelectedWeek?.correction_requested && (
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200" data-testid="correction-pending-badge">
                  Correction Requested
                </Badge>
              )}
            </div>
            {submissionForSelectedWeek?.correction_requested && (
              <p className="w-full text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="correction-pending-note">
                <strong>Correction pending admin review:</strong> {submissionForSelectedWeek.correction_reason}
              </p>
            )}
            {submissionForSelectedWeek?.status === 'rejected' && submissionForSelectedWeek.review_note && (
              <p className="w-full text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="rejection-note">
                {submissionForSelectedWeek.review_note.startsWith('Correction approved:') ? (
                  <><strong>Reopened for correction:</strong> {submissionForSelectedWeek.review_note.replace('Correction approved:', '').trim()}</>
                ) : (
                  <><strong>Returned by admin:</strong> {submissionForSelectedWeek.review_note}</>
                )}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100 text-blue-600">
                  <Clock className="w-5 h-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total Hours</p>
                  <p className="text-xl font-bold">{summary.total_hours}h</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100 text-green-600">
                  <Calendar className="w-5 h-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total Entries</p>
                  <p className="text-xl font-bold">{summary.total_entries}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-100 text-purple-600">
                  <User className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Employees Tracked</p>
                  <p className="text-xl font-bold">{summary.by_employee?.length || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-orange-100 text-orange-600">
                  <Briefcase className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Active Jobs</p>
                  <p className="text-xl font-bold">{activeJobs.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className="text-xs">Start Date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="filter-start-date"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">End Date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="filter-end-date"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Job</Label>
              <Select value={selectedJob} onValueChange={setSelectedJob}>
                <SelectTrigger data-testid="filter-job">
                  <SelectValue placeholder="All Jobs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Jobs</SelectItem>
                  {activeJobs.map((job) => (
                    <SelectItem key={job.id} value={job.id}>{job.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Employee</Label>
              <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                <SelectTrigger data-testid="filter-employee">
                  <SelectValue placeholder="All Employees" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Employees</SelectItem>
                  {allEmployees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Time Entries Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            Time Entries
            {!loading && (
              <Badge variant="secondary" className="ml-2">
                {entries.length} entries
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1,2,3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">No time entries found</p>
              <p className="text-sm mt-1">Add your first time entry to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-2 font-medium">Date</th>
                    <th className="text-left py-3 px-2 font-medium">Employee</th>
                    <th className="text-left py-3 px-2 font-medium">Job</th>
                    <th className="text-left py-3 px-2 font-medium">Time</th>
                    <th className="text-right py-3 px-2 font-medium">Hours</th>
                    <th className="text-left py-3 px-2 font-medium">Notes</th>
                    <th className="text-right py-3 px-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr key={entry.id} className="border-b hover:bg-muted/50" data-testid={`entry-${entry.id}`}>
                      <td className="py-3 px-2">
                        <span className="font-medium">
                          {format(new Date(entry.date + 'T00:00:00'), 'MMM d, yyyy')}
                        </span>
                      </td>
                      <td className="py-3 px-2">{entry.employee_name}</td>
                      <td className="py-3 px-2">
                        <span className="truncate max-w-[150px] inline-block">{entry.job_title}</span>
                      </td>
                      <td className="py-3 px-2">
                        <span className="text-muted-foreground">
                          {formatTime(entry.start_time)} - {formatTime(entry.end_time)}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-right">
                        <Badge variant="outline">{entry.hours}h</Badge>
                      </td>
                      <td className="py-3 px-2">
                        <span className="truncate max-w-[150px] inline-block text-muted-foreground">
                          {entry.notes || '-'}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-right">
                        <div className="flex justify-end gap-1 items-center">
                          {entry.status && entry.status !== 'draft' && (
                            <Badge
                              variant="outline"
                              className={
                                entry.status === 'approved' ? 'bg-green-50 text-green-700 border-green-200 mr-1' :
                                entry.status === 'submitted' ? 'bg-blue-50 text-blue-700 border-blue-200 mr-1' :
                                'bg-red-50 text-red-700 border-red-200 mr-1'
                              }
                              data-testid={`entry-status-${entry.id}`}
                            >
                              {entry.status === 'submitted' ? 'Pending' : entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                            </Badge>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={!isAdmin && (entry.status === 'submitted' || entry.status === 'approved')}
                            onClick={() => handleOpenDialog(entry)}
                            data-testid={`edit-entry-${entry.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={!isAdmin && (entry.status === 'submitted' || entry.status === 'approved')}
                            onClick={() => handleDelete(entry.id)}
                            data-testid={`delete-entry-${entry.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Employee Hours Summary */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Employee Hours Summary
            {employeeHoursSummary && (
              <Badge variant="secondary" className="ml-2">
                {employeeHoursSummary.total_hours}h total
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!employeeHoursSummary ? (
            <div className="space-y-3">
              {[1,2,3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : employeeHoursSummary.employees?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>No hours logged yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {employeeHoursSummary.employees.map((emp) => (
                <div key={emp.employee_id} className="border rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExpandedEmployee(expandedEmployee === emp.employee_id ? null : emp.employee_id)}
                    className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
                    data-testid={`employee-summary-${emp.employee_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-primary/10">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium">{emp.employee_name}</p>
                        <p className="text-sm text-muted-foreground">{emp.job_count} job(s)</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="text-lg font-bold">
                        {emp.total_hours}h
                      </Badge>
                      <ChevronRight className={`w-5 h-5 text-muted-foreground transition-transform ${
                        expandedEmployee === emp.employee_id ? 'rotate-90' : ''
                      }`} />
                    </div>
                  </button>
                  {expandedEmployee === emp.employee_id && (
                    <div className="border-t bg-muted/30 p-4">
                      <p className="text-xs text-muted-foreground uppercase font-medium mb-2">Hours by Job</p>
                      <div className="space-y-2">
                        {emp.jobs.map((job) => (
                          <div key={job.job_id} className="flex items-center justify-between py-2 px-3 bg-background rounded">
                            <span className="text-sm">{job.job_title}</span>
                            <Badge variant="secondary">{job.hours}h</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen} modal={true}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingEntry ? 'Edit Time Entry' : 'Add Time Entry'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!editingEntry && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="job_id">Job *</Label>
                  <Select
                    value={formData.job_id}
                    onValueChange={(value) => setFormData({ ...formData, job_id: value, employee_ids: [] })}
                  >
                    <SelectTrigger data-testid="form-job">
                      <SelectValue placeholder="Select a job" />
                    </SelectTrigger>
                    <SelectContent position="popper" className="z-[100]">
                      <SelectItem value={NO_JOB} data-testid="form-job-no-job">
                        <div className="flex flex-col">
                          <span className="font-medium">No Job</span>
                          <span className="text-xs text-muted-foreground">Non-billable time / expenses only</span>
                        </div>
                      </SelectItem>
                      {activeJobs.map((job) => (
                        <SelectItem key={job.id} value={job.id}>
                          <div className="flex flex-col">
                            <span>{job.title}</span>
                            <span className="text-xs text-muted-foreground">{job.customer_name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {activeJobs.length === 0 && !isNoJob && (
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      No active jobs available
                    </p>
                  )}
                  {isNoJob && (
                    <p className="text-xs text-orange-600 flex items-center gap-1" data-testid="no-job-helper">
                      <AlertCircle className="w-3 h-3" />
                      Not tied to a customer job. Non-Billable hours or Reimbursed Expenses required.
                    </p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Employees *</Label>
                    {formData.job_id && jobEmployees.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleSelectAllEmployees}
                        className="h-auto py-1 px-2 text-xs"
                      >
                        {formData.employee_ids.length === jobEmployees.length ? 'Deselect All' : 'Select All'}
                      </Button>
                    )}
                  </div>
                  
                  {!formData.job_id ? (
                    <div className="p-3 border rounded-lg text-sm text-muted-foreground bg-muted/50">
                      Select a job first
                    </div>
                  ) : jobEmployees.length === 0 ? (
                    <div className="p-3 border rounded-lg">
                      <p className="text-xs text-amber-600 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {isNoJob ? 'No employees available' : 'No employees assigned to this job'}
                      </p>
                    </div>
                  ) : (
                    <div className="border rounded-lg max-h-[150px] overflow-y-auto">
                      {jobEmployees.map((emp) => (
                        <label
                          key={emp.id}
                          className="flex items-center gap-3 px-3 py-2 hover:bg-muted/50 cursor-pointer border-b last:border-b-0"
                        >
                          <Checkbox
                            checked={formData.employee_ids.includes(emp.id)}
                            onCheckedChange={() => handleEmployeeToggle(emp.id)}
                            data-testid={`employee-checkbox-${emp.id}`}
                          />
                          <span className="text-sm">{emp.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  
                  {formData.employee_ids.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {formData.employee_ids.length} employee{formData.employee_ids.length > 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>
              </>
            )}
            
            {editingEntry && (
              <div className="p-3 bg-muted rounded-lg text-sm">
                <p><strong>Job:</strong> {editingEntry.job_id ? editingEntry.job_title : 'No Job (Non-billable / Expenses only)'}</p>
                <p><strong>Employee:</strong> {editingEntry.employee_name}</p>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">{editingEntry ? 'Date *' : 'Start Date *'}</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  data-testid="form-start-date"
                />
              </div>
              {!editingEntry && (
                <div className="space-y-2">
                  <Label htmlFor="end_date">End Date *</Label>
                  <Input
                    id="end_date"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    data-testid="form-end-date"
                  />
                </div>
              )}
            </div>

            {/* Day of Week Selection - only show when date range spans multiple days */}
            {!editingEntry && formData.start_date && formData.end_date && formData.start_date !== formData.end_date && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Days of Week *</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (formData.selected_days.length === 7) {
                        setFormData({ ...formData, selected_days: [1, 2, 3, 4, 5] }); // Weekdays only
                      } else if (formData.selected_days.length === 5 && 
                                 formData.selected_days.every(d => [1,2,3,4,5].includes(d))) {
                        setFormData({ ...formData, selected_days: [] }); // None
                      } else {
                        setFormData({ ...formData, selected_days: [0, 1, 2, 3, 4, 5, 6] }); // All
                      }
                    }}
                    className="h-auto py-1 px-2 text-xs"
                  >
                    {formData.selected_days.length === 7 ? 'Weekdays Only' : 
                     formData.selected_days.length === 0 ? 'Select All' : 'Select All'}
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {dayNames.map((day) => (
                    <label
                      key={day.value}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border cursor-pointer transition-colors ${
                        formData.selected_days.includes(day.value)
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background hover:bg-muted border-input'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={formData.selected_days.includes(day.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ ...formData, selected_days: [...formData.selected_days, day.value].sort() });
                          } else {
                            setFormData({ ...formData, selected_days: formData.selected_days.filter(d => d !== day.value) });
                          }
                        }}
                      />
                      <span className="text-sm font-medium">{day.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {!editingEntry && formData.start_date && formData.end_date && formData.start_date !== formData.end_date && (
              <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-xs text-blue-700 dark:text-blue-300 text-center">
                {(() => {
                  const start = new Date(formData.start_date);
                  const end = new Date(formData.end_date);
                  let matchingDays = 0;
                  let current = new Date(start);
                  while (current <= end) {
                    if (formData.selected_days.includes(current.getDay())) {
                      matchingDays++;
                    }
                    current.setDate(current.getDate() + 1);
                  }
                  const selectedDayNames = formData.selected_days
                    .sort()
                    .map(d => dayNames.find(dn => dn.value === d)?.label)
                    .join(', ');
                  return matchingDays > 0 
                    ? `${matchingDays} entries will be created (${selectedDayNames})${formData.employee_ids.length > 1 ? ` × ${formData.employee_ids.length} employees` : ''}`
                    : 'No matching days in selected range';
                })()}
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_time">Start Time *</Label>
                <Input
                  id="start_time"
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  data-testid="form-start-time"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_time">End Time *</Label>
                <Input
                  id="end_time"
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  data-testid="form-end-time"
                />
              </div>
            </div>
            
            {formData.start_time && formData.end_time && (
              <div className="p-3 bg-primary/10 rounded-lg text-sm text-center">
                <span className="font-medium">Hours per day: </span>
                <span className="text-primary font-bold">
                  {calculateHours(formData.start_time, formData.end_time)}h
                </span>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Optional notes..."
                rows={2}
                data-testid="form-notes"
              />
            </div>

            {/* Non-Billable Time Section - Main Form */}
            <div className="border rounded-lg p-3 bg-orange-50/50 dark:bg-orange-950/20">
              <Label className="text-sm font-medium text-orange-600 flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-muted-foreground" />
                Non-Billable Time {isNoJob && <span className="text-red-500">*</span>}
              </Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="non_billable_hours">Hours {isNoJob && <span className="text-red-500">*</span>}</Label>
                  <Input
                    id="non_billable_hours"
                    type="number"
                    step="0.5"
                    min="0"
                    value={formData.non_billable_hours}
                    onChange={(e) => setFormData({ ...formData, non_billable_hours: e.target.value })}
                    placeholder="0.0"
                    data-testid="form-non-billable-hours"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="non_billable_reason">Reason</Label>
                  <Input
                    id="non_billable_reason"
                    value={formData.non_billable_reason}
                    onChange={(e) => setFormData({ ...formData, non_billable_reason: e.target.value })}
                    placeholder="e.g., Travel, Training"
                    data-testid="form-non-billable-reason"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Hours not billed to customer (travel time, training, etc.)
              </p>
            </div>

            {/* Timesheet Extra Fields — hidden for "No Job" entries since they aren't tied to a customer job */}
            {!isNoJob && (
            <div className="border rounded-lg">
              <div className="px-3 py-2 text-sm font-medium flex items-center gap-2 bg-muted/40 rounded-t-lg">
                <Truck className="w-4 h-4" />
                Timesheet Fields
                <span className="text-xs text-muted-foreground ml-auto">For Excel export</span>
              </div>
              <div className="p-3 border-t space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="perdiem">Per Diem ($)</Label>
                    <Input
                      id="perdiem"
                      type="number"
                      step="0.01"
                      value={formData.perdiem}
                      onChange={(e) => setFormData({ ...formData, perdiem: e.target.value })}
                      placeholder="0.00"
                      data-testid="form-perdiem"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="co_worker">Co-Worker</Label>
                    <Input
                      id="co_worker"
                      value={formData.co_worker}
                      onChange={(e) => setFormData({ ...formData, co_worker: e.target.value })}
                      placeholder="Co-worker name"
                      data-testid="form-co-worker"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="truck_number">Truck #</Label>
                    <Input
                      id="truck_number"
                      value={formData.truck_number}
                      onChange={(e) => setFormData({ ...formData, truck_number: e.target.value })}
                      placeholder="Truck number"
                      data-testid="form-truck-number"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="odometer">Odometer</Label>
                    <Input
                      id="odometer"
                      type="number"
                      value={formData.odometer}
                      onChange={(e) => setFormData({ ...formData, odometer: e.target.value })}
                      placeholder="Mileage"
                      data-testid="form-odometer"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="wex_receipts">Wex Receipts</Label>
                    <Select
                      value={formData.wex_receipts || "none"}
                      onValueChange={(v) => setFormData({ ...formData, wex_receipts: v === "none" ? "" : v })}
                    >
                      <SelectTrigger data-testid="form-wex-receipts">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="Yes">Yes</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="po_number">PO #</Label>
                    <Input
                      id="po_number"
                      value={formData.po_number}
                      onChange={(e) => setFormData({ ...formData, po_number: e.target.value })}
                      placeholder="Purchase order #"
                      data-testid="form-po-number"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="job_location">Job Location</Label>
                  <Input
                    id="job_location"
                    value={formData.job_location}
                    onChange={(e) => setFormData({ ...formData, job_location: e.target.value })}
                    placeholder="City, State (overrides job location)"
                    data-testid="form-job-location"
                  />
                </div>

                {/* Mileage Section */}
                <div className="border-t pt-4 mt-4">
                  <Label className="text-sm font-medium text-blue-600 flex items-center gap-2 mb-3">
                    <Truck className="w-4 h-4" />
                    Mileage
                  </Label>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="starting_mileage">Starting Mileage</Label>
                      <Input
                        id="starting_mileage"
                        type="number"
                        min="0"
                        value={formData.starting_mileage}
                        onChange={(e) => setFormData({ ...formData, starting_mileage: e.target.value })}
                        placeholder="0"
                        data-testid="form-starting-mileage"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="miles_driven">Miles Driven</Label>
                      <Input
                        id="miles_driven"
                        type="number"
                        step="0.1"
                        min="0"
                        value={formData.miles_driven}
                        onChange={(e) => setFormData({ ...formData, miles_driven: e.target.value })}
                        placeholder="0.0"
                        data-testid="form-miles-driven"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="mileage_charged">Charged ($)</Label>
                      <Input
                        id="mileage_charged"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.mileage_charged}
                        onChange={(e) => setFormData({ ...formData, mileage_charged: e.target.value })}
                        placeholder="0.00"
                        data-testid="form-mileage-charged"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Reimbursed Expenses — STANDALONE (independent of Timesheet Fields / days of the week) */}
            <div className="border rounded-lg p-3 bg-emerald-50/40 dark:bg-emerald-950/20">
              <Label className="text-sm font-medium text-green-600 flex items-center gap-2 mb-3">
                <DollarSign className="w-4 h-4" />
                Reimbursed Expenses
              </Label>
              <div className="space-y-3">
                {formData.expenses.map((expense, index) => (
                  <div key={expense.id || `expense-${index}`} className="border rounded-lg p-3 bg-muted/30">
                    <div className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-3 space-y-1">
                        <Label className="text-xs">Date</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              className="w-full justify-start text-left font-normal h-9 px-3"
                              data-testid={`expense-date-${index}`}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
                              {expense.date && isValid(parseISO(expense.date))
                                ? format(parseISO(expense.date), 'M/d/yyyy')
                                : <span className="text-muted-foreground">Pick date</span>}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <CalendarPicker
                              mode="single"
                              selected={expense.date && isValid(parseISO(expense.date)) ? parseISO(expense.date) : undefined}
                              onSelect={(date) => {
                                if (!date) return;
                                const iso = format(date, 'yyyy-MM-dd');
                                const newExpenses = [...formData.expenses];
                                newExpenses[index] = { ...newExpenses[index], date: iso };
                                setFormData({ ...formData, expenses: newExpenses });
                              }}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="col-span-4 space-y-1">
                        <Label className="text-xs">Purpose</Label>
                        <Input
                          value={expense.purpose || ''}
                          onChange={(e) => {
                            const newExpenses = [...formData.expenses];
                            newExpenses[index] = { ...newExpenses[index], purpose: e.target.value };
                            setFormData({ ...formData, expenses: newExpenses });
                          }}
                          placeholder="What was purchased"
                          data-testid={`expense-purpose-${index}`}
                        />
                      </div>
                      <div className="col-span-2 space-y-1">
                        <Label className="text-xs">Amount ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={expense.amount || ''}
                          onChange={(e) => {
                            const newExpenses = [...formData.expenses];
                            newExpenses[index] = { ...newExpenses[index], amount: parseFloat(e.target.value) || 0 };
                            setFormData({ ...formData, expenses: newExpenses });
                          }}
                          placeholder="0.00"
                          data-testid={`expense-amount-${index}`}
                        />
                      </div>
                      <div className="col-span-2 space-y-1">
                        <Label className="text-xs">Receipt</Label>
                        {expense.receipt_id ? (
                          <div className="flex items-center gap-1">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-9 flex-1 text-xs px-2"
                              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${expense.receipt_url}`, '_blank')}
                              data-testid={`expense-view-receipt-${index}`}
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              View
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-9 w-9 text-red-500 hover:text-red-700"
                              onClick={() => handleRemoveReceipt(index)}
                              data-testid={`expense-remove-receipt-${index}`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="relative">
                            <input
                              type="file"
                              accept="image/*,application/pdf"
                              onChange={(e) => handleReceiptUpload(index, e.target.files[0])}
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                              disabled={uploadingReceipt === index}
                              data-testid={`expense-upload-receipt-${index}`}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-9 w-full text-xs"
                              disabled={uploadingReceipt === index}
                            >
                              {uploadingReceipt === index ? (
                                <>
                                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  Uploading...
                                </>
                              ) : (
                                <>
                                  <Paperclip className="w-3 h-3 mr-1" />
                                  Attach
                                </>
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                      <div className="col-span-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            const newExpenses = formData.expenses.filter((_, i) => i !== index);
                            setFormData({ ...formData, expenses: newExpenses });
                          }}
                          className="h-9 w-9 text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`expense-remove-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {expense.receipt_filename && (
                      <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1">
                        <Image className="w-3 h-3" />
                        {expense.receipt_filename}
                      </div>
                    )}
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setFormData({
                      ...formData,
                      expenses: [...formData.expenses, { id: `exp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, date: '', purpose: '', amount: 0 }]
                    });
                  }}
                  className="w-full mt-2"
                  data-testid="add-expense-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Expense
                </Button>
                {formData.expenses.length > 0 && (
                  <div className="text-right text-sm font-medium text-muted-foreground">
                    Total: ${expensesFormTotal.toFixed(2)}
                  </div>
                )}
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="form-submit">
                {submitting ? 'Saving...' : editingEntry ? 'Update' : 'Add Entry'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payroll Export Dialog */}
      <Dialog open={payrollDialogOpen} onOpenChange={setPayrollDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="payroll-export-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              Export Payroll Report
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Quick Date Presets */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Quick Select</Label>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPayrollPreset('last-month')}
                  data-testid="preset-last-month"
                >
                  Last Month
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPayrollPreset('this-month')}
                  data-testid="preset-this-month"
                >
                  This Month
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPayrollPreset('last-2-weeks')}
                  data-testid="preset-last-2-weeks"
                >
                  Last 2 Weeks
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setPayrollPreset('last-week')}
                  data-testid="preset-last-week"
                >
                  Last Week
                </Button>
              </div>
            </div>

            {/* Custom Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="payroll-start">Start Date</Label>
                <Input
                  id="payroll-start"
                  type="date"
                  value={payrollDates.start}
                  onChange={(e) => setPayrollDates(prev => ({ ...prev, start: e.target.value }))}
                  data-testid="payroll-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="payroll-end">End Date</Label>
                <Input
                  id="payroll-end"
                  type="date"
                  value={payrollDates.end}
                  onChange={(e) => setPayrollDates(prev => ({ ...prev, end: e.target.value }))}
                  data-testid="payroll-end-date"
                />
              </div>
            </div>

            {/* Info Box */}
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm">
              <p className="text-blue-700 dark:text-blue-300">
                <strong>Report includes:</strong>
              </p>
              <ul className="mt-1 text-blue-600 dark:text-blue-400 text-xs space-y-0.5">
                <li>• Employee hours breakdown (regular vs overtime)</li>
                <li>• Job and project details</li>
                <li>• Cost center allocation</li>
                <li>• Pay calculations (if hourly rates set)</li>
                <li>• Overtime: Hours over 8/day counted as OT</li>
              </ul>
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setPayrollDialogOpen(false)}
              disabled={payrollExporting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleExportPayrollCSV}
              disabled={payrollExporting}
              data-testid="export-csv-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              {payrollExporting ? 'Exporting...' : 'Export CSV'}
            </Button>
            <Button
              type="button"
              onClick={handleExportPayrollPDF}
              disabled={payrollExporting}
              className="bg-green-600 hover:bg-green-700"
              data-testid="export-pdf-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              {payrollExporting ? 'Exporting...' : 'Export PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Timesheet Export Dialog */}
      <Dialog open={timesheetDialogOpen} onOpenChange={setTimesheetDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="timesheet-export-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-blue-600" />
              Generate Weekly Timesheet
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Employee and Week Selection */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Employee</Label>
                <Select 
                  value={timesheetData.employee_id} 
                  onValueChange={(v) => {
                    setTimesheetData(prev => ({ ...prev, employee_id: v }));
                    setTimesheetPreview(null);
                  }}
                >
                  <SelectTrigger data-testid="timesheet-employee-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {allEmployees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Week Ending (Sunday)</Label>
                <Input
                  type="date"
                  value={timesheetData.week_ending}
                  onChange={(e) => {
                    setTimesheetData(prev => ({ ...prev, week_ending: e.target.value }));
                    setTimesheetPreview(null);
                  }}
                  data-testid="timesheet-week-ending"
                />
              </div>
            </div>

            {/* Preview Button */}
            <Button
              type="button"
              variant="outline"
              onClick={handleTimesheetPreview}
              disabled={!timesheetData.employee_id || !timesheetData.week_ending}
              className="w-full"
              data-testid="timesheet-preview-btn"
            >
              Load Preview
            </Button>

            {/* Preview Data */}
            {timesheetPreview && (
              <div className="space-y-4 border rounded-lg p-4 bg-muted/30">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-semibold">{timesheetPreview.employee.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      Week: {timesheetPreview.week_start} to {timesheetPreview.week_ending}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm">
                      <span className="font-medium">Total Hours: </span>
                      <span className="text-green-600">{timesheetPreview.totals.total_hours}</span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Billable: {timesheetPreview.totals.billable_hours} | 
                      Non-Billable: {timesheetPreview.totals.non_billable_hours}
                    </p>
                  </div>
                </div>

                {/* Daily Breakdown Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-1">Day</th>
                        <th className="text-left py-2 px-1">Date</th>
                        <th className="text-right py-2 px-1">Bill</th>
                        <th className="text-right py-2 px-1">Non-Bill</th>
                        <th className="text-right py-2 px-1">Perdiem</th>
                        <th className="text-left py-2 px-1">Customer</th>
                        <th className="text-left py-2 px-1">Location</th>
                      </tr>
                    </thead>
                    <tbody>
                      {timesheetPreview.days.map((day) => (
                        <tr key={day.day} className="border-b hover:bg-muted/50">
                          <td className="py-2 px-1 font-medium">{day.day}</td>
                          <td className="py-2 px-1">{day.date}</td>
                          <td className="py-2 px-1 text-right text-green-600">
                            {day.billable_hours || '-'}
                          </td>
                          <td className="py-2 px-1 text-right text-orange-600">
                            {day.non_billable_hours || '-'}
                          </td>
                          <td className="py-2 px-1 text-right">
                            {day.perdiem ? `$${day.perdiem}` : '-'}
                          </td>
                          <td className="py-2 px-1">{day.customer || '-'}</td>
                          <td className="py-2 px-1">{day.job_location || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Reimbursed Expenses Breakdown */}
                {timesheetPreview.expenses && timesheetPreview.expenses.length > 0 && (
                  <div className="border rounded-lg p-3" data-testid="preview-expenses-section">
                    <h4 className="text-sm font-semibold mb-2 text-green-700 dark:text-green-400">
                      Reimbursed Expenses
                    </h4>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-1.5 px-1">Date</th>
                          <th className="text-left py-1.5 px-1">Purpose</th>
                          <th className="text-right py-1.5 px-1">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedPreviewExpenses.map((exp, idx) => (
                            <tr key={exp.id || `${exp.date}-${exp.purpose}-${idx}`} className="border-b hover:bg-muted/50">
                              <td className="py-1.5 px-1">{exp.date || '-'}</td>
                              <td className="py-1.5 px-1">{exp.purpose || '-'}</td>
                              <td className="py-1.5 px-1 text-right text-green-600">
                                ${(exp.amount || 0).toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        <tr className="font-semibold">
                          <td className="py-1.5 px-1" colSpan={2}>Expense Total</td>
                          <td className="py-1.5 px-1 text-right text-green-600" data-testid="preview-expense-total">
                            ${(timesheetPreview.totals.expenses || 0).toFixed(2)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Info Box */}
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm">
                  <p className="text-blue-700 dark:text-blue-300">
                    <strong>Note:</strong> The Excel export will use your company&apos;s timesheet template format.
                  </p>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                    Additional fields (Truck #, Odometer, Co-Worker, etc.) can be added when creating time entries.
                  </p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setTimesheetDialogOpen(false)}
              disabled={timesheetExporting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleExportTimesheet}
              disabled={timesheetExporting || !timesheetData.employee_id || !timesheetData.week_ending}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="export-timesheet-xlsx-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              {timesheetExporting ? 'Exporting...' : 'Export Excel Timesheet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Correction Dialog */}
      <Dialog open={correctionDialogOpen} onOpenChange={setCorrectionDialogOpen}>
        <DialogContent data-testid="correction-request-dialog">
          <DialogHeader>
            <DialogTitle>Request Timesheet Correction</DialogTitle>
            <DialogDescription>
              Your timesheet for week ending {submitWeekEnding} has been approved and is locked.
              Describe what needs to be corrected — an admin will review your request and reopen the timesheet if approved.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Textarea
              value={correctionReason}
              onChange={(e) => setCorrectionReason(e.target.value)}
              placeholder="e.g., Wednesday's hours should be 10, not 8 — forgot to log overtime"
              rows={4}
              data-testid="correction-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCorrectionDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleRequestCorrection}
              disabled={requestingCorrection || !correctionReason.trim()}
              data-testid="submit-correction-request-btn"
            >
              {requestingCorrection ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Send Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
